"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const facts = [{
  label: "OBSERVER",
  value: "Harlan"
}, {
  label: "BASE",
  value: "Vernon, BC"
}, {
  label: "COORDINATES",
  value: "50° 16′ N · 119° 16′ W"
}, {
  label: "SKIES",
  value: "Bortle 5"
}, {
  label: "IMAGING SINCE",
  value: "2022"
}, {
  label: "DAY JOB",
  value: "Industrial Automation"
}];
const interests = ["Astrophotography", "Programming", "Sim & VR", "Guitar"];
export default function Coord01TheObserver() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2461" id="coord-01-the-observer" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-44 px-6 md:px-12 lg:px-16 border-t border-border">
      {/* ambient glows, matching the other sections */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-secondary/5 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-accent/5 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl">
        {/* Header */}
        <div ref={headerRef} className={`mb-16 lg:mb-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-px bg-secondary" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-secondary">
              COORD 01 — THE OBSERVER
            </span>
          </div>
          <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-foreground">
            Observe. Record.{" "}
            <span className="text-ring">Contribute.</span>
          </h2>
        </div>

        {/* Body grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Left: fact card */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 bg-card/40 backdrop-blur-md border border-border rounded-md p-7">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-6">
                STATION RECORD
              </p>
              <div className="border-t border-border/60">
                {facts.map((f, i) => <div key={i} data-index={i} className="grid grid-cols-12 gap-3 py-4 border-b border-border/60">
                    <span className="col-span-5 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground pt-0.5">
                      {f.label}
                    </span>
                    <span className="col-span-7 font-default text-sm text-foreground">
                      {f.value}
                    </span>
                  </div>)}
              </div>

              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-7 mb-3">
                THE BIG FOUR
              </p>
              <div className="flex flex-wrap gap-2">
                {interests.map((tag, i) => <span key={i} data-index={i} className="font-mono text-[10px] uppercase tracking-[0.12em] text-foreground/80 border border-border/60 rounded px-2.5 py-1">
                    {tag}
                  </span>)}
              </div>
            </div>
          </aside>

          {/* Right: prose */}
          <div ref={bodyRef} className={`lg:col-span-8 transition-all duration-1000 ease-out delay-200 ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {/* Lead */}
            <p className="font-serif text-foreground" style={{
            fontSize: "1.1875rem",
            lineHeight: 1.75
          }}>
              <span className="font-default font-black float-left mr-3 mt-1 text-[var(--corona-orange)]" style={{
              fontSize: "4.5rem",
              lineHeight: 0.85,
              letterSpacing: "-0.04em"
            }}>
                S
              </span>
              olObservatory is a one-person setup — me, a tripod, and a few telescopes in a backyard in Vernon, BC, under Bortle 5 skies. No dome, no observatory building. The gear comes out when the weather cooperates and the kids are down for the night. I started shooting deep-sky in 2022 and have since added solar imaging in hydrogen-alpha, comets when they show up, and whatever else is worth losing sleep over.
            </p>

            {/* Background */}
            <div className="mt-12 flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-secondary">BACKGROUND</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <p className="mt-5 font-serif text-foreground" style={{
            fontSize: "1.1875rem",
            lineHeight: 1.75
          }}>
              My day job is industrial automation. I'm a Red Seal electrician working on the PLC and SCADA systems that keep water and wastewater plants running. Most of that work comes down to monitoring and logging — watching readings over time and catching the moment something shifts. Turns out that's most of astrophotography too, just aimed at the sky instead of a pump station.
            </p>

            {/* Off the clock */}
            <div className="mt-12 flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-secondary">OFF THE CLOCK</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <p className="mt-5 font-serif text-foreground" style={{
            fontSize: "1.1875rem",
            lineHeight: 1.75
          }}>Outside of work it's mostly the same handful of things: astrophotography, writing code, a VR rig I use for flight, racing and space sims, and a guitar that gets picked up in fits and starts. I've also got a wife and two daughters who seem to like my cool space pics, so that's nice!</p>

            {/* The logbook */}
            <div className="mt-12 flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-secondary">THE LOGBOOK</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <p className="mt-5 font-serif text-foreground" style={{
            fontSize: "1.1875rem",
            lineHeight: 1.75
          }}>
              This site is the logbook. It's where the images live, where I write up the gear and the screwups honestly, and where I'm building the next project out in the open. The nights that fell apart get posted right next to the ones that worked.
            </p>

            {/* ObsLogs callout */}
            <div className="mt-12 border-l-2 border-[var(--corona-orange)] bg-card/30 rounded-r-md pl-6 pr-6 py-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--corona-orange)] mb-3">
                STATUS — IN DEVELOPMENT
              </p>
              <p className="font-serif text-foreground" style={{
              fontSize: "1.1875rem",
              lineHeight: 1.7
            }}>
                Every clear night, amateur imagers pull in a pile of genuinely useful data — and most of it never goes anywhere. I'm working on fixing that. Think eBird, but for astrophotography. More on it when it's closer.
              </p>
            </div>

            {/* Signature */}
            <div className="mt-14 pt-8 border-t border-border">
              <p className="font-default font-bold uppercase text-foreground inline-block border-b border-[var(--corona-orange)] pb-1" style={{
              fontSize: "0.6875rem",
              letterSpacing: "0.18em"
            }}>
                Harlan &middot; SolObservatory &middot; Vernon, BC &middot; 50&deg; 16&prime; N
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}